import { gql } from "@apollo/client";
import { useEffect } from "react";

export const CartFragment = gql`
  fragment CartFragment on Cart {
    id
    checkoutUrl
    cost {
      totalTaxAmount {
        amount
        currencyCode
      }
      subtotalAmount {
        amount
        currencyCode
      }
      totalAmount {
        amount
        currencyCode
      }
    }
    discountCodes {
      code
      applicable
    }
    lines(first: 250) {
      edges {
        node {
          id
          quantity
          merchandise {
            ... on ProductVariant {
              id
              title
              product {
                title
              }
              image {
                url
              }
              price {
                amount
                currencyCode
              }
            }
          }
          attributes {
            key
            value
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            amountPerQuantity {
              amount
              currencyCode
            }
          }
          discountAllocations {
            discountedAmount {
              amount
              currencyCode
            }
            discountApplication {
              allocationMethod
              targetSelection
              targetType
              value {
                ... on PricingPercentageValue {
                  percentage
                }
                ... on MoneyV2 {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const applyDiscount = gql`
  mutation cartDiscountCodeApply($cartId: ID!, $discountCode: String!) {
    cartDiscountCodeApply(cartId: $cartId, discountCode: $discountCode) {
      userErrors {
        message
        field
      }
      cart {
        ...CartFragment
      }
    }
  }
  ${CartFragment}
`;



export const createCart = gql`
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      userErrors {
        message
        field
      }
      cart {
        ...CartFragment
      }
    }
  }
  ${CartFragment}
`;

export const cartLinesAdd = gql`
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      userErrors {
        message
        field
      }
      cart {
        ...CartFragment
      }
    }
  }
  ${CartFragment}
`;

export const cartLinesUpdate = gql`
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      userErrors {
        message
        field
      }
      cart {
        ...CartFragment
      }
    }
  }
  ${CartFragment}
`;

export const cartLinesRemove = gql`
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      userErrors {
        message
        field
      }
      cart {
        ...CartFragment
      }
    }
  }
  ${CartFragment}
`;

export const cartBuyerIdentityUpdate = gql`
  mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      userErrors {
        message
        field
      }
      cart {
        ...CartFragment
      }
    }
  }
  ${CartFragment}
`;

export function useCheckoutEffect(
  data,
  key,
  setDataCallback,
  resetToNewCheckout
) {
  useEffect(() => {
    if (data && data[key]) {
      if (data[key]["userErrors"] && data[key]["userErrors"].length > 0) {
        let Raven = window.Raven || { captureException: () => {} };
        Raven.captureException(data);

        // TODO: does this error check need to
        // be more granular?
        resetToNewCheckout();
      } else if (data[key].cart) {
        setDataCallback(data[key].cart);
      }
    }
  }, [data]);
}

export function checkMutationError(data, key) {
  if (data && data[key]) {
    if (data[key].userErrors.length > 0) {
      return true;
    }
  }

  return false;
}

// make a generic way to listen for the checkout complete error
// we'll call the apollo client checkout reset
export function checkoutCompleteErrorEffect(error, errorCallback) {
  useEffect(() => {
    if (error && error.message) {
      if (error.message === "Checkout is already completed.") {
        errorCallback();
      } else {
        let Raven = window.Raven || { captureException: () => {} };
        Raven.captureException(error);

        throw error;
      }
    }
  }, [error]);
}
